// Journey catalogue cards. These components render only the server-authored catalogue and wire controls to
// the controller hook; they load nothing and decide no execution mode. Replay is labelled scripted because
// that is the only mode the v1 POST contract can admit.

import type { JourneyCatalogueItem, JourneyCatalogueView } from '../journey-lab-api.ts';
import type { JourneyLiveControlView } from '../journey-live-api.ts';
import type { JourneyLiveOperation } from '../useJourneyLive.ts';
import {
  journeyAvailableModeLabels,
  journeyModeLabel,
  journeyModeProofBoundary,
  journeyStamp,
  journeyStatusLabel,
  journeyTone,
  shortDefinitionHash,
} from '../journey-lab-view.ts';
import { JOURNEY_AVAILABILITY_PROOF_BOUNDARY } from '../journey-language.ts';
import { journeyLiveModeName } from '../journey-live-view.ts';
import { Btn, EmptyState, Panel, SectionHead, StatusPill } from '../primitives.tsx';

export interface JourneyCatalogueProps {
  readonly view: JourneyCatalogueView;
  readonly selectedRunId: string | null;
  readonly replayingScenarioId: string | null;
  readonly onSelectRun: (runId: string) => void;
  readonly onReplay: (scenarioId: string) => void;
  readonly liveControl: JourneyLiveControlView | null;
  readonly liveOperation: JourneyLiveOperation | null;
  readonly onRunLive: (scenarioId: string) => void;
}

function CatalogueMetadata({
  journey,
}: {
  readonly journey: JourneyCatalogueItem;
}): React.JSX.Element {
  return (
    <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
      <div>
        <dt className="text-text-subtle">Source</dt>
        <dd className="mt-1 text-text-muted">
          {journey.source === 'real_anonymised' ? 'Real · anonymised' : 'Synthetic'}
        </dd>
      </div>
      <div>
        <dt className="text-text-subtle">Definition</dt>
        <dd className="ritona-numeric mt-1 text-text-muted" title={journey.definitionHash}>
          {shortDefinitionHash(journey.definitionHash)}
        </dd>
      </div>
      <div>
        <dt className="text-text-subtle">Available checks</dt>
        <dd className="mt-1 text-text-muted">
          {journey.supportedExecutionModes.flatMap(journeyAvailableModeLabels).join(', ')}
          <span className="mt-1 block text-text-subtle">{JOURNEY_AVAILABILITY_PROOF_BOUNDARY}</span>
        </dd>
      </div>
      {journey.sourceRef !== undefined ? (
        <div>
          <dt className="text-text-subtle">Source reference</dt>
          <dd className="ritona-numeric mt-1 text-text-muted">{journey.sourceRef}</dd>
        </div>
      ) : null}
    </dl>
  );
}

function ControlList({
  journey,
}: {
  readonly journey: JourneyCatalogueItem;
}): React.JSX.Element | null {
  if (journey.controls.length === 0) return null;
  return (
    <ul aria-label={`Controls covered by ${journey.title}`} className="mt-3 flex flex-wrap gap-2">
      {journey.controls.map((control) => (
        <li
          key={control}
          className="ritona-numeric rounded-sm border border-border px-2 py-1 text-xs text-text-muted"
        >
          {control}
        </li>
      ))}
    </ul>
  );
}

function LatestRunSummary({
  journey,
}: {
  readonly journey: JourneyCatalogueItem;
}): React.JSX.Element {
  const latest = journey.latestRun;
  if (latest === undefined) {
    return (
      <p className="mt-3 border-t border-border pt-3 text-xs text-text-subtle">
        No run recorded yet.
      </p>
    );
  }
  return (
    <div
      className="mt-3 border-t border-border pt-3 text-xs text-text-subtle"
      data-latest-run={latest.runId}
    >
      <span>{journeyModeLabel(latest.executionMode, latest.liveMode)}</span>
      <span aria-hidden="true"> · </span>
      <time dateTime={latest.requestedAt}>{journeyStamp(latest.requestedAt)}</time>
      <span className="mt-1 block">
        {journeyModeProofBoundary(latest.executionMode, latest.liveMode)}
      </span>
      {latest.definitionChanged ? (
        <span className="mt-1 block text-clay">Definition changed since this run.</span>
      ) : null}
    </div>
  );
}

interface JourneyCardProps extends Omit<JourneyCatalogueProps, 'view'> {
  readonly journey: JourneyCatalogueItem;
}

interface LiveJourneyButtonProps {
  readonly journey: JourneyCatalogueItem;
  readonly visible: boolean;
  readonly disabled: boolean;
  readonly label: string;
  readonly starting: boolean;
  readonly onRunLive: (scenarioId: string) => void;
}

function LatestJourneyRunButton({
  journey,
  onSelectRun,
}: Pick<JourneyCardProps, 'journey' | 'onSelectRun'>): React.JSX.Element | null {
  const latest = journey.latestRun;
  if (latest === undefined) return null;
  return (
    <Btn
      kind="quiet"
      sm
      ariaLabel={`Inspect latest run for ${journey.title}`}
      onClick={() => onSelectRun(latest.runId)}
    >
      Inspect latest
    </Btn>
  );
}

function ScriptedReplayButton({
  journey,
  replayingScenarioId,
  liveOperation,
  onReplay,
}: Pick<
  JourneyCardProps,
  'journey' | 'replayingScenarioId' | 'liveOperation' | 'onReplay'
>): React.JSX.Element {
  return (
    <Btn
      kind="ghost"
      sm
      disabled={replayingScenarioId !== null || liveOperation !== null}
      ariaLabel={`Run scripted check for ${journey.title}`}
      onClick={() => onReplay(journey.id)}
    >
      {replayingScenarioId === journey.id ? 'Queueing…' : 'Run scripted check'}
    </Btn>
  );
}

function LiveJourneyButton({
  journey,
  visible,
  disabled,
  label,
  starting,
  onRunLive,
}: LiveJourneyButtonProps): React.JSX.Element | null {
  if (!visible) return null;
  return (
    <Btn
      sm
      disabled={disabled}
      ariaLabel={`${label} for ${journey.title}`}
      onClick={() => onRunLive(journey.id)}
    >
      {starting ? 'Starting simulator…' : label}
    </Btn>
  );
}

function JourneyActions(props: JourneyCardProps): React.JSX.Element {
  const {
    journey,
    replayingScenarioId,
    onSelectRun,
    onReplay,
    liveControl,
    liveOperation,
    onRunLive,
  } = props;
  const supportsLive = journey.supportedExecutionModes.includes('live');
  const liveArmed = liveControl?.state === 'armed' && liveControl.armMode !== undefined;
  const liveLabel =
    liveControl?.armMode === undefined
      ? 'Run full-flow check'
      : `Run ${journeyLiveModeName(liveControl.armMode)}`;
  return (
    <>
      <div className="mt-4 flex flex-wrap gap-2">
        <LatestJourneyRunButton journey={journey} onSelectRun={onSelectRun} />
        <ScriptedReplayButton
          journey={journey}
          replayingScenarioId={replayingScenarioId}
          liveOperation={liveOperation}
          onReplay={onReplay}
        />
        <LiveJourneyButton
          journey={journey}
          visible={supportsLive}
          disabled={!liveArmed || liveOperation !== null || replayingScenarioId !== null}
          label={liveLabel}
          starting={liveOperation === 'start'}
          onRunLive={onRunLive}
        />
      </div>
      {supportsLive && !liveArmed ? (
        <p className="mt-2 text-xs text-text-subtle">
          Complete preflight and arm one full-flow mode above to enable this check.
        </p>
      ) : null}
    </>
  );
}

function JourneyCard(props: JourneyCardProps): React.JSX.Element {
  const { journey, selectedRunId } = props;
  const latest = journey.latestRun;
  return (
    <Panel
      className={`p-4${latest?.runId === selectedRunId ? ' border-forest' : ''}`}
      dataMarker={{ name: 'journey', value: journey.id }}
    >
      <article aria-labelledby={`journey-${journey.id}-title`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="ritona-numeric text-xs uppercase tracking-wide text-forest">
              {journey.id} · {journey.stepCount} simulated steps · {journey.assertionCount}{' '}
              assertions
            </div>
            {journey.liveStepCount !== undefined && journey.liveAssertionCount !== undefined ? (
              <div className="ritona-numeric mt-1 text-xs text-text-subtle">
                {journey.liveStepCount} full-flow steps · {journey.liveAssertionCount} assertions
              </div>
            ) : null}
            <h3
              id={`journey-${journey.id}-title`}
              className="mt-1 text-base font-semibold text-text"
            >
              {journey.title}
            </h3>
          </div>
          {latest !== undefined ? (
            <StatusPill tone={journeyTone(latest.state, latest.verdict)}>
              {journeyStatusLabel(latest.state, latest.verdict)}
            </StatusPill>
          ) : null}
        </div>
        <p className="mt-2 text-sm leading-relaxed text-text-muted">{journey.narrative}</p>
        <CatalogueMetadata journey={journey} />
        <ControlList journey={journey} />
        <LatestRunSummary journey={journey} />
        <JourneyActions {...props} />
      </article>
    </Panel>
  );
}

/** The scenario catalogue: one card per authored scenario, with the latest durable run when one exists. */
export function JourneyCatalogue(props: JourneyCatalogueProps): React.JSX.Element {
  const { view, ...cardProps } = props;
  return (
    <section aria-label="Journey catalogue">
      <SectionHead label="Scenario catalogue" />
      {view.journeys.length === 0 ? (
        <EmptyState>No scenarios are available in the journey pack.</EmptyState>
      ) : (
        <div className="space-y-4">
          {view.journeys.map((journey) => (
            <JourneyCard key={journey.id} journey={journey} {...cardProps} />
          ))}
        </div>
      )}
    </section>
  );
}
